import React, { useMemo, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Plus, Trash2, ArrowUp, ArrowDown, AlertTriangle, Code2, Type, Eye, UploadCloud, FileCode, X } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { SheetBody, SheetFooter } from '@/components/ui/sheet'
import { createCampaign } from '../services/campaignsApi'
import { renderMergeFields } from '../utils/recipients'
import { stepLabel, stepReferencePhrase } from '../utils/steps'
import RecipientPicker from './RecipientPicker'
import type { Campaign, CampaignRecipientInput, CampaignStep, CampaignStepBodyType } from '../types'

type Props = {
  workspaceId: string
  // Lives inside the Campaigns page's slide-over (not a routed page), so it
  // reports outcomes via callbacks instead of navigating itself.
  onCreated: (campaign: Campaign) => void
  onCancel: () => void
}

function emptyStep(order: number): CampaignStep {
  return { order, delayDays: order === 0 ? 0 : 3, subject: '', body: '', bodyType: 'text' }
}

const SAMPLE_FIELDS_FALLBACK = { firstName: 'Jane', lastName: 'Doe', email: 'jane@example.com' }

// Wraps a pasted-in HTML fragment with a minimal document shell when it isn't one
// already — the live preview iframe needs a full document to render consistently
// (otherwise a bare "<p>...</p>" fragment can render with browser-default margins
// that don't match what an email client would actually do).
function wrapForPreview(html: string): string {
  if (/<html[\s>]/i.test(html)) return html
  return `<!doctype html><html><body style="font-family: Arial, sans-serif; font-size: 14px; color: #111; margin: 0; padding: 12px;">${html}</body></html>`
}

const BODY_TYPE_META: Record<CampaignStepBodyType, { label: string; icon: React.ElementType }> = {
  text: { label: 'Plain text', icon: Type },
  html: { label: 'HTML', icon: Code2 },
}

const StepCard: React.FC<{
  step: CampaignStep
  index: number
  stepCount: number
  sampleFields: { firstName?: string | undefined; lastName?: string | undefined; email?: string | undefined }
  onUpdate: (patch: Partial<CampaignStep>) => void
  onRemove: () => void
  onMove: (direction: -1 | 1) => void
}> = ({ step, index, stepCount, sampleFields, onUpdate, onRemove, onMove }) => {
  const { currentTheme: t } = useTheme()
  const bodyType = step.bodyType ?? 'text'
  const previewSubject = renderMergeFields(step.subject, sampleFields)
  const previewBody = renderMergeFields(step.body, sampleFields)

  const htmlFileInputRef = useRef<HTMLInputElement>(null)
  const [isDraggingHtml, setIsDraggingHtml] = useState(false)
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)

  const loadHtmlFile = (file: File) => {
    setUploadError(null)
    const looksLikeHtml = file.name.toLowerCase().endsWith('.html') || file.name.toLowerCase().endsWith('.htm') || file.type === 'text/html'
    if (!looksLikeHtml) {
      setUploadError('That doesn\'t look like an .html file.')
      return
    }
    const reader = new FileReader()
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : ''
      onUpdate({ body: text })
      setUploadedFileName(file.name)
    }
    reader.onerror = () => setUploadError('Couldn\'t read that file.')
    reader.readAsText(file)
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.18 }}
      className="rounded-xl p-3.5 flex flex-col gap-2.5"
      style={{ backgroundColor: t.sectionBackground, border: `1px solid ${t.border}` }}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-[12px] font-semibold" style={{ color: t.text }}>
          {stepLabel(index)}{index > 0 ? ` — ${step.delayDays} day${step.delayDays === 1 ? '' : 's'} after ${stepReferencePhrase(index - 1)}` : ' — sent immediately on approval'}
        </span>
        <div className="flex items-center gap-1">
          {/* Index 0 is the Initial Email, rendered in its own fixed section
              above — these controls only ever reach follow-ups (index > 0),
              and a follow-up can never move into the initial-email slot. */}
          {index > 1 && (
            <button type="button" onClick={() => onMove(-1)} aria-label="Move up" className="p-1 rounded-md" style={{ color: t.mutedText }}>
              <ArrowUp size={13} />
            </button>
          )}
          {index > 0 && index < stepCount - 1 && (
            <button type="button" onClick={() => onMove(1)} aria-label="Move down" className="p-1 rounded-md" style={{ color: t.mutedText }}>
              <ArrowDown size={13} />
            </button>
          )}
          {index > 0 && (
            <button type="button" onClick={onRemove} aria-label="Remove follow-up" className="p-1 rounded-md" style={{ color: t.errorText }}>
              <Trash2 size={13} />
            </button>
          )}
        </div>
      </div>

      {index > 0 && (
        <div className="flex items-center gap-2">
          <label className="text-[11.5px]" style={{ color: t.mutedText }}>Wait</label>
          <input
            type="number"
            min={1}
            value={step.delayDays}
            onChange={(e) => onUpdate({ delayDays: Math.max(1, Number(e.target.value) || 1) })}
            className="w-16 h-7 text-[12px] px-2 rounded-md border outline-none"
            style={{ backgroundColor: t.hoverBackground, borderColor: t.border, color: t.text }}
          />
          <label className="text-[11.5px]" style={{ color: t.mutedText }}>days, then send</label>
        </div>
      )}

      <input
        value={step.subject}
        onChange={(e) => onUpdate({ subject: e.target.value })}
        placeholder="Subject — {{firstName}} is a valid merge field"
        className="w-full h-11 text-sm px-3.5 rounded-lg border outline-none"
        style={{ backgroundColor: t.hoverBackground, borderColor: t.border, color: t.text }}
      />

      {/* Plain text / HTML mode toggle — same segmented-control look used across
          the app (e.g. Settings → Automation's mode toggle). */}
      <div className="inline-flex items-center gap-1 rounded-lg p-1 self-start" style={{ backgroundColor: t.hoverBackground, border: `1px solid ${t.border}` }}>
        {(['text', 'html'] as const).map((type) => {
          const isActive = bodyType === type
          const { label, icon: Icon } = BODY_TYPE_META[type]
          return (
            <button
              key={type}
              type="button"
              onClick={() => {
                if (type === bodyType) return
                // Body content from one mode is never meaningful in the other
                // (raw HTML source showing up as a "plain text" body, or vice
                // versa) — switching modes starts that step's body fresh.
                onUpdate({ bodyType: type, body: '' })
                setUploadedFileName(null)
                setUploadError(null)
              }}
              className="flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[11px] font-medium transition-colors"
              style={{ backgroundColor: isActive ? t.accent : 'transparent', color: isActive ? t.buttonText : t.secondrytext }}
            >
              <Icon size={11} />
              {label}
            </button>
          )
        })}
      </div>

      {bodyType === 'html' ? (
        <div
          onDragOver={(e) => { e.preventDefault(); setIsDraggingHtml(true) }}
          onDragLeave={() => setIsDraggingHtml(false)}
          onDrop={(e) => {
            e.preventDefault()
            setIsDraggingHtml(false)
            const file = e.dataTransfer.files[0]
            if (file) loadHtmlFile(file)
          }}
          onClick={() => htmlFileInputRef.current?.click()}
          className="relative flex flex-col items-center justify-center gap-2 min-h-[140px] rounded-lg border-2 border-dashed cursor-pointer transition-colors text-center px-4"
          style={{
            borderColor: isDraggingHtml ? t.accent : t.border,
            backgroundColor: isDraggingHtml ? t.hoverBackground : 'transparent',
          }}
        >
          {uploadedFileName ? (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onUpdate({ body: '' })
                  setUploadedFileName(null)
                  setUploadError(null)
                }}
                aria-label="Clear uploaded file"
                title="Clear uploaded file"
                className="absolute top-2 right-2 p-1 rounded-md"
                style={{ color: t.mutedText }}
              >
                <X size={14} />
              </button>
              <FileCode size={20} style={{ color: t.mutedText }} />
              <p className="text-[12.5px] font-medium" style={{ color: t.text }}>{uploadedFileName}</p>
              <p className="text-[11px]" style={{ color: t.mutedText }}>Click or drop another file to replace it</p>
            </>
          ) : (
            <>
              <UploadCloud size={20} style={{ color: t.mutedText }} />
              <p className="text-[12.5px] font-medium" style={{ color: t.text }}>Drop an .html file here, or click to browse</p>
              <p className="text-[11px]" style={{ color: t.mutedText }}>
                {'{{firstName}} / {{lastName}} / {{email}} merge fields are supported in the file'}
              </p>
            </>
          )}
          {uploadError && <span className="text-[11px]" style={{ color: t.errorText }}>{uploadError}</span>}
          <input
            ref={htmlFileInputRef}
            type="file"
            accept=".html,.htm,text/html"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) loadHtmlFile(file)
              e.target.value = ''
            }}
          />
        </div>
      ) : (
        <textarea
          value={step.body}
          onChange={(e) => onUpdate({ body: e.target.value })}
          placeholder="Body — plain text, {{firstName}}/{{lastName}}/{{email}} merge fields supported"
          className="w-full min-h-[140px] text-[13px] px-2.5 py-2 rounded-lg border outline-none resize-y"
          style={{ backgroundColor: t.hoverBackground, borderColor: t.border, color: t.text }}
        />
      )}

      <button
        type="button"
        onClick={() => setPreviewOpen(true)}
        disabled={!step.body.trim()}
        className="self-start flex items-center gap-1.5 h-7 px-2.5 rounded-lg text-[11.5px] font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ backgroundColor: t.hoverBackground, color: t.text, border: `1px solid ${t.border}` }}
      >
        <Eye size={12} />
        View preview
      </button>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{stepLabel(index)} preview</DialogTitle>
          </DialogHeader>
          <p className="text-[11.5px] -mt-2" style={{ color: t.mutedText }}>
            Rendered {sampleFields.email ? `as ${sampleFields.firstName ?? sampleFields.email}` : 'with sample data'} — this is what the recipient will actually see.
          </p>
          <div className="rounded-lg p-2.5" style={{ backgroundColor: t.hoverBackground }}>
            <p className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: t.mutedText }}>Subject</p>
            <p className="text-[13.5px] font-medium" style={{ color: t.text }}>{previewSubject || <span style={{ color: t.mutedText }}>(no subject)</span>}</p>
          </div>
          {bodyType === 'html' ? (
            <iframe
              title={`${stepLabel(index)} preview`}
              sandbox=""
              srcDoc={step.body.trim() ? wrapForPreview(previewBody) : '<p style="color:#9CA3AF;font-family:Arial,sans-serif;font-size:13px;padding:12px">Nothing to preview yet.</p>'}
              className="w-full min-h-[360px] rounded-lg border"
              style={{ borderColor: t.border, backgroundColor: '#fff' }}
            />
          ) : (
            <p className="text-[13px] whitespace-pre-wrap p-2.5 rounded-lg" style={{ color: t.text, backgroundColor: t.hoverBackground }}>
              {previewBody}
            </p>
          )}
        </DialogContent>
      </Dialog>
    </motion.div>
  )
}

const CampaignBuilderForm: React.FC<Props> = ({ workspaceId, onCreated, onCancel }) => {
  const { currentTheme: t } = useTheme()

  const [name, setName] = useState('')
  const [steps, setSteps] = useState<CampaignStep[]>([emptyStep(0)])
  const [dailySendCap, setDailySendCap] = useState(50)
  const [recipients, setRecipients] = useState<CampaignRecipientInput[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const sampleFields = useMemo(() => {
    const first = recipients[0]
    return first ? { firstName: first.firstName, lastName: first.lastName, email: first.email } : SAMPLE_FIELDS_FALLBACK
  }, [recipients])

  const updateStep = (index: number, patch: Partial<CampaignStep>) => {
    setSteps((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)))
  }

  const addStep = () => {
    setSteps((prev) => [...prev, emptyStep(prev.length)])
  }

  const removeStep = (index: number) => {
    setSteps((prev) => prev.filter((_, i) => i !== index).map((s, i) => ({ ...s, order: i })))
  }

  const moveStep = (index: number, direction: -1 | 1) => {
    setSteps((prev) => {
      const target = index + direction
      // Index 0 is the fixed Initial Email slot — a follow-up can never move into it.
      if (target < 1 || target >= prev.length) return prev
      const copy = [...prev]
      ;[copy[index], copy[target]] = [copy[target]!, copy[index]!]
      return copy.map((s, i) => ({ ...s, order: i }))
    })
  }

  const missingFields: string[] = []
  if (!name.trim()) missingFields.push('a campaign name')
  if (steps.some((s) => !s.subject.trim())) missingFields.push('a subject for every email')
  if (steps.some((s) => !s.body.trim())) missingFields.push('a body for every email')
  if (recipients.length === 0) missingFields.push('at least one recipient')

  const canSubmit = missingFields.length === 0 && dailySendCap > 0 && !submitting

  const handleSubmit = async () => {
    setSubmitting(true)
    setSubmitError(null)
    try {
      const campaign = await createCampaign(workspaceId, { name: name.trim(), steps, dailySendCap, recipients })
      onCreated(campaign)
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to create campaign')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <SheetBody className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <label className="text-[12.5px] font-semibold" style={{ color: t.text }}>Campaign name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Q4 Enterprise Outreach"
            className="w-full h-11 text-sm px-3.5 rounded-lg border outline-none"
            style={{ backgroundColor: t.hoverBackground, borderColor: t.border, color: t.text }}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-[12.5px] font-semibold" style={{ color: t.text }}>Initial email</label>
          <StepCard
            step={steps[0]!}
            index={0}
            stepCount={steps.length}
            sampleFields={sampleFields}
            onUpdate={(patch) => updateStep(0, patch)}
            onRemove={() => {}}
            onMove={() => {}}
          />
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-[12.5px] font-semibold" style={{ color: t.text }}>Sequence</label>
              <p className="text-[11.5px] mt-0.5" style={{ color: t.mutedText }}>Follow-ups sent after the initial email.</p>
            </div>
            <button
              type="button"
              onClick={addStep}
              className="flex items-center gap-1 h-7 px-2.5 rounded-lg text-[11.5px] font-medium shrink-0"
              style={{ backgroundColor: t.hoverBackground, color: t.text, border: `1px solid ${t.border}` }}
            >
              <Plus size={12} />
              Add follow-up
            </button>
          </div>

          <AnimatePresence initial={false}>
            {steps.slice(1).map((step, i) => {
              const index = i + 1
              return (
                <StepCard
                  key={index}
                  step={step}
                  index={index}
                  stepCount={steps.length}
                  sampleFields={sampleFields}
                  onUpdate={(patch) => updateStep(index, patch)}
                  onRemove={() => removeStep(index)}
                  onMove={(direction) => moveStep(index, direction)}
                />
              )
            })}
          </AnimatePresence>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-[12.5px] font-semibold" style={{ color: t.text }}>Recipients</label>
          <RecipientPicker workspaceId={workspaceId} recipients={recipients} onChange={setRecipients} />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-[12.5px] font-semibold" style={{ color: t.text }}>Daily send cap</label>
          <p className="text-[12px]" style={{ color: t.mutedText }}>
            Max emails this campaign sends per 24 hours — a per-campaign limit, not workspace-wide.
          </p>
          <input
            type="number"
            min={1}
            value={dailySendCap}
            onChange={(e) => setDailySendCap(Math.max(1, Number(e.target.value) || 1))}
            className="w-28 h-11 text-sm px-3.5 rounded-lg border outline-none"
            style={{ backgroundColor: t.hoverBackground, borderColor: t.border, color: t.text }}
          />
        </div>
      </SheetBody>

      <SheetFooter className="flex-col items-stretch gap-2">
        {submitError && (
          <div className="flex items-center gap-2 text-[12.5px]" style={{ color: t.errorText }}>
            <AlertTriangle size={14} />
            {submitError}
          </div>
        )}
        {!canSubmit && !submitting && missingFields.length > 0 && (
          <p className="text-[11.5px]" style={{ color: t.mutedText }}>
            Still need: {missingFields.join(', ')}.
          </p>
        )}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
            className="flex items-center gap-1.5 h-9 px-4 rounded-lg text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: t.accentGradient ?? t.accent }}
          >
            {submitting ? 'Creating…' : 'Create campaign'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="h-9 px-4 rounded-lg text-sm font-medium"
            style={{ backgroundColor: t.hoverBackground, color: t.text, border: `1px solid ${t.border}` }}
          >
            Cancel
          </button>
        </div>
      </SheetFooter>
    </>
  )
}

export default React.memo(CampaignBuilderForm)
